/**
 * Campos del formulario de centro de trabajo (compartido).
 *
 * Encapsula la cascada GeoNames (país → división → ciudad), el pin GPS, las
 * coordenadas a mano, el radio de geovalla y los toggles avanzados. El estado
 * geográfico vive DENTRO del componente; el único contrato con el padre es
 * `value` + `onChange`. Lo consumen el panel (`WorksitesAdmin`) y el wizard de
 * onboarding (`WelcomeWizard`).
 *
 * `variant==='wizard'` oculta los campos avanzados (plantilla mínima, persona
 * de contacto y el toggle de QR de pantalla) para no alargar el alta inicial.
 */
import { useEffect, useRef, useState } from 'react';
import {
  geoCountries, geoDivisions, geoPlaces, geoAddress, getTenantStatus,
  GeoCountry, GeoDivision, GeoPlace, AddressHit, WorksiteFormValue,
} from '../../services/apiClient';
import { Icon } from '../ui/Icon';
import { LocationMap } from './LocationMap';

// La profundidad la decide el país (guía GeoNames): ES para en provincia
// (nivel 2, salta CCAA); el resto usa nivel 1. No se asume.
function divisionLevel(country: string): number { return country === 'ES' ? 2 : 1; }

// GeoNames devuelve "Provincia de Albacete", "Ciudad Autónoma de Ceuta"…
// Nos quedamos con el nombre a secas para el desplegable.
function cleanDivision(name: string): string {
  return name
    .replace(/^Provincia de\s+/i, '')
    .replace(/^Ciudad Autónoma de\s+/i, '')
    .replace(/^Comunidad (Autónoma |Foral )?(de |del )?/i, '')
    .trim();
}

function divisionLabel(country: string): string {
  if (country === 'AD') return 'Parroquia';
  return 'Provincia';
}

export function WorksiteFormFields({
  value, onChange, variant = 'panel',
}: { value: WorksiteFormValue; onChange: (patch: Partial<WorksiteFormValue>) => void; variant?: 'panel' | 'wizard' }) {
  const [countries, setCountries] = useState<GeoCountry[]>([]);
  const [divisions, setDivisions] = useState<GeoDivision[]>([]);
  const [places, setPlaces] = useState<GeoPlace[]>([]);
  const [divCode, setDivCode] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);
  const [locError, setLocError] = useState('');

  // Al montar: países + país por defecto del tenant.
  useEffect(() => {
    if (countries.length) return;
    geoCountries().then(setCountries).catch(() => undefined);
    getTenantStatus().then((t) => { if (t.country && !value.country) onChange({ country: t.country }); }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cambiar de país recarga sus divisiones (con el nivel que ese país usa).
  useEffect(() => {
    setDivisions([]); setPlaces([]); setDivCode('');
    if (!value.country) return;
    setGeoLoading(true);
    geoDivisions(value.country, divisionLevel(value.country)).then(setDivisions).catch(() => undefined).finally(() => setGeoLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.country]);

  const onDivision = (code: string) => {
    setDivCode(code); setPlaces([]);
    const div = divisions.find((d) => (d.admin2_code || d.admin1_code || d.code) === code);
    onChange({ province: div ? cleanDivision(div.name) : undefined });
    if (!value.country || !code) return;
    const key = value.country === 'ES' ? { admin2_code: code } : { admin1_code: code };
    setGeoLoading(true);
    geoPlaces(value.country, key).then(setPlaces).catch(() => undefined).finally(() => setGeoLoading(false));
  };

  const onCity = (name: string) => {
    onChange({ city: name });
    const p = places.find((x) => x.name === name);
    // El centroide de la ciudad ubica el centro en el mapa. La PRECISIÓN de la
    // geovalla no sale de aquí: sale del pin GPS o de lat/lon a mano.
    if (p?.coordinates) onChange({ geo_latitude: Number(p.coordinates.lat.toFixed(7)), geo_longitude: Number(p.coordinates.lng.toFixed(7)) });
  };

  const useMyLocation = () => {
    navigator.geolocation?.getCurrentPosition(
      (pos) => onChange({ geo_latitude: Number(pos.coords.latitude.toFixed(7)), geo_longitude: Number(pos.coords.longitude.toFixed(7)) }),
      () => setLocError('No se pudo obtener tu ubicación'),
    );
  };

  // Autocompletado de dirección (Nominatim). Debounce 400 ms — su política es
  // máx 1 req/s, y no queremos disparar por cada tecla.
  const [addrHits, setAddrHits] = useState<AddressHit[]>([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onAddressType = (text: string) => {
    onChange({ address: text });
    if (debounce.current) clearTimeout(debounce.current);
    if (text.trim().length < 3) { setAddrHits([]); return; }
    debounce.current = setTimeout(() => {
      geoAddress(text.trim(), value.country || undefined)
        .then((hits) => { setAddrHits(hits); setAddrOpen(true); })
        .catch(() => setAddrHits([]));
    }, 400);
  };

  const pickAddress = (h: AddressHit) => {
    // Una dirección elegida trae el punto PRECISO: rellena calle, ciudad, CP y
    // fija las coordenadas exactas. El pin del mapa se mueve solo.
    onChange({
      address: [h.road, h.house_number].filter(Boolean).join(' ') || h.label.split(',')[0],
      city: h.city ?? value.city,
      postal_code: h.postcode ?? value.postal_code,
      province: h.province ?? value.province,
      geo_latitude: Number(h.latitude.toFixed(7)),
      geo_longitude: Number(h.longitude.toFixed(7)),
    });
    setAddrOpen(false);
  };

  const radius = value.geo_radius_meters ?? 0;

  // Legible: a partir de 1 km se dice en km, no «2500 m».
  const radioLegible = (m: number) =>
    m >= 1000 ? `${(m / 1000).toLocaleString('es-ES', { maximumFractionDigits: 2 })} km` : `${m} m`;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
        <label className="block">
          <span className="label">Nombre</span>
          <input className="field" placeholder="Obra Sant Cugat" value={value.name} onChange={(e) => onChange({ name: e.target.value })} />
        </label>
        <label className="block">
          <span className="label">Código</span>
          <input className="field" placeholder="OSC" value={value.code} onChange={(e) => onChange({ code: e.target.value.toUpperCase() })} />
        </label>
        <label className="block">
          <span className="label">País</span>
          <select className="field" value={value.country ?? ''} onChange={(e) => onChange({ country: e.target.value || undefined })}>
            <option value="">Selecciona…</option>
            {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label">{value.country ? divisionLabel(value.country) : 'Provincia'}</span>
          <select
            className="field"
            value={divCode}
            disabled={!divisions.length}
            onChange={(e) => onDivision(e.target.value)}
          >
            <option value="">{geoLoading && !divisions.length ? 'Cargando…' : 'Selecciona…'}</option>
            {divisions.map((d) => {
              const code = d.admin2_code || d.admin1_code || d.code;
              return <option key={code} value={code}>{cleanDivision(d.name)}</option>;
            })}
          </select>
        </label>
        <label className="block">
          <span className="label">Ciudad</span>
          <select
            className="field"
            value={value.city ?? ''}
            disabled={!places.length}
            onChange={(e) => onCity(e.target.value)}
          >
            <option value="">{geoLoading && !places.length ? 'Cargando…' : 'Selecciona…'}</option>
            {places.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label">Código postal</span>
          <input className="field" value={value.postal_code ?? ''} onChange={(e) => onChange({ postal_code: e.target.value })} />
        </label>
        <label className="block md:col-span-2 relative">
          <span className="label">Dirección (calle y número)</span>
          <input
            className="field"
            placeholder="Empieza a escribir: La Rambla 91, Barcelona…"
            value={value.address ?? ''}
            onChange={(e) => onAddressType(e.target.value)}
            onFocus={() => addrHits.length && setAddrOpen(true)}
            onBlur={() => setTimeout(() => setAddrOpen(false), 150)}
            autoComplete="off"
          />
          {addrOpen && addrHits.length > 0 && (
            <ul className="absolute z-20 left-0 right-0 mt-1 bg-surface border border-line-strong rounded-xl overflow-hidden shadow-lg max-h-[240px] overflow-y-auto">
              {addrHits.map((h, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickAddress(h)}
                    className="w-full text-left px-3.5 py-2.5 text-[13.5px] hover:bg-surface-2 flex items-start gap-2"
                  >
                    <Icon name="pin" size={15} strokeWidth={1.8} className="text-brand shrink-0 mt-0.5" />
                    <span className="min-w-0">{h.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <span className="text-[12px] text-ink-3 mt-1 block">
            Elige una sugerencia y el pin del mapa se coloca solo, con coordenadas precisas.
          </span>
        </label>
        {variant === 'panel' && (
          <label className="block">
            <span className="label">Plantilla mínima</span>
            <input
              className="field" type="number" min={0}
              value={value.min_staff ?? ''}
              onChange={(e) => onChange({ min_staff: e.target.value === '' ? undefined : Number(e.target.value) })}
            />
          </label>
        )}
        {variant === 'panel' && (
          <label className="block">
            <span className="label">Persona de contacto</span>
            <input className="field" value={value.contact_person_name ?? ''} onChange={(e) => onChange({ contact_person_name: e.target.value })} />
          </label>
        )}
      </div>

      <div className="rounded-xl border border-line p-4">
        <div className="flex items-center justify-between mb-2.5 flex-wrap gap-2">
          <span className="font-semibold text-[15px]">Geovalla</span>
          {/* Radio exacto editable a mano: el slider acaba en 5 km, pero el
              backend admite hasta 100 000 m (schema ge=10, le=100000) — una
              obra lineal, un puerto o un polígono grande no podían configurar
              su geovalla desde el panel (ronda 3, r2b10[9]). Con clamp al
              rango del schema: 0 = sin geovalla. */}
          <label className="flex items-center gap-1.5">
            <input
              className="field font-mono text-[13px] w-[92px] text-right" type="number" min={0} max={100000}
              aria-label="Radio de la geovalla en metros"
              value={radius}
              onChange={(e) => {
                const n = Number(e.target.value);
                const clamped = Number.isNaN(n) ? 0 : Math.max(0, Math.min(100000, Math.round(n)));
                onChange({ geo_radius_meters: clamped });
              }}
            />
            <span className="font-mono text-[12.5px] font-semibold text-brand">m</span>
          </label>
        </div>
        <input
          type="range" min={0} max={5000} step={10} value={Math.min(radius, 5000)}
          onChange={(e) => onChange({ geo_radius_meters: Number(e.target.value) })}
          className="w-full accent-[var(--brand)]"
        />
        <div className="flex justify-between font-mono text-[11px] text-ink-3 mt-1">
          <span>0 m (sin geovalla)</span>
          <span>5 km{radius > 5000 ? ` · ${radioLegible(radius)} (a mano)` : ''}</span>
        </div>
        {0 < radius && radius < 10 && (
          // El schema exige ge=10: avisamos en lugar de clamp silencioso —
          // un clamp mentiría sobre lo que se guardó.
          <p className="text-[12.5px] text-warn mt-1.5">El radio mínimo es 10 m (0 = sin geovalla).</p>
        )}
        <p className="text-[13px] text-ink-2 mt-2.5">
          {radius === 0
            ? 'Este centro (p. ej. una oficina) no validará ubicación al fichar.'
            : `Se podrá fichar dentro de ${radioLegible(radius)} del punto. Fuera, el fichaje se bloquea con aviso.`}
        </p>
        {radius > 0 && (
          <div className="mt-3 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={useMyLocation} className="h-10 px-3.5 rounded-lg border border-line-strong text-[13.5px] font-semibold flex items-center gap-2">
                <Icon name="pin" size={16} strokeWidth={1.8} />
                Fijar el pin aquí (estoy en el centro)
              </button>
              {value.geo_latitude == null && (
                <span className="text-[12.5px] text-warn">Sin coordenadas la geovalla no se activa.</span>
              )}
              {(value.geo_latitude != null || value.geo_longitude != null) &&
                (value.geo_latitude == null || value.geo_longitude == null) && (
                  <span className="text-[12.5px] text-warn">
                    Faltan coordenadas: latitud y longitud van juntas, con una sola no se activa.
                  </span>
                )}
            </div>

            {/* Coordenadas: editables a mano para la obra sin dirección. */}
            <div className="grid grid-cols-2 gap-3 max-w-[420px]">
              <label className="block">
                <span className="label">Latitud</span>
                <input
                  className="field font-mono text-[14px]" inputMode="decimal" placeholder="41.5610000"
                  value={value.geo_latitude ?? ''}
                  onChange={(e) => onChange({ geo_latitude: e.target.value === '' ? undefined : Number(e.target.value) })}
                />
              </label>
              <label className="block">
                <span className="label">Longitud</span>
                <input
                  className="field font-mono text-[14px]" inputMode="decimal" placeholder="2.0089000"
                  value={value.geo_longitude ?? ''}
                  onChange={(e) => onChange({ geo_longitude: e.target.value === '' ? undefined : Number(e.target.value) })}
                />
              </label>
            </div>

            {/* El mapa: arrastra el pin o pulsa para el punto exacto. */}
            <LocationMap
              latitude={value.geo_latitude ?? undefined}
              longitude={value.geo_longitude ?? undefined}
              radius={radius}
              onPick={(lat, lng) => onChange({ geo_latitude: lat, geo_longitude: lng })}
            />

            <p className="text-[12.5px] text-ink-3">
              <strong className="text-ink-2">Arrastra el pin</strong> del mapa (o pulsa) hasta el punto exacto.
              También puedes buscar la dirección arriba, usar <strong className="text-ink-2">"fijar el pin aquí"</strong>{' '}
              estando en el centro, o escribir la latitud y longitud a mano — útil para una obra sin dirección.
            </p>
          </div>
        )}
      </div>

      {variant === 'panel' && (
        <div className="rounded-xl border border-line p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <button
              type="button"
              role="switch"
              aria-checked={!!value.require_dynamic_qr}
              onClick={() => onChange({ require_dynamic_qr: !value.require_dynamic_qr })}
              className={`flex-none w-12 h-7 rounded-full relative transition-colors mt-0.5 ${value.require_dynamic_qr ? 'bg-brand' : 'bg-surface-3'}`}
            >
              <span
                className="absolute top-[3px] w-[22px] h-[22px] rounded-full bg-white shadow transition-all"
                style={{ left: value.require_dynamic_qr ? 23 : 3 }}
              />
            </button>
            <span className="flex-1">
              <span className="block font-semibold text-[15px]">QR de pantalla (anti-foto)</span>
              <span className="block text-[13.5px] text-ink-2">
                Exige el código rotatorio de una <strong className="text-ink">tablet o pantalla en el centro</strong>:
                hay que estar delante al fichar, una foto del QR no vale. Déjalo apagado si usas un QR impreso en papel
                (entonces la geovalla es la que protege).
              </span>
            </span>
          </label>
        </div>
      )}

      {locError && <p className="text-[12.5px] text-danger">{locError}</p>}
    </div>
  );
}
